import { flow, types } from 'mobx-state-tree';
import { getEnvironment } from './utils';
import jwt_decode from 'jwt-decode';
import Cookie from 'js-cookie';
import { ActionCode, Page } from '../common/enums';

export type AuthorizationType = 'all' | 'atLeastOne';

export const AuthenticationStore = types
  .model('AuthenticationStore', {
    isAuthenticating: false,
    authToken: types.maybe(types.string),
    impersonated: false,
    // userActionCodes: types.optional(types.array(types.string), []),
  })
  .views((self) => {
    const getDecodedToken = () => (self.authToken ? jwt_decode(self.authToken) : {});
    const getActionCodes = () => {
      const decoded = jwt_decode(self.authToken);
      let actions = decoded.role as string[];
      if (actions == undefined) {
        actions = new Array();
      } else if (typeof actions === 'string') {
        actions = new Array(actions);
      }
      return actions;
    };

    return {
      get isLoggedIn() {
        return self.authToken !== null;
      },
      get isImpersonated() {
        return self.impersonated;
      },
      get userName() {
        return getDecodedToken().unique_name || '';
      },
      get userId(): number | undefined {
        return +getDecodedToken().USER_ID || undefined;
      },
      get userActionCodes(): string[] {
        return getActionCodes();
      },
      get pharmacyId(): number | null {
        return +getDecodedToken().PharmacyId || null;
      },
      get secretariatId(): number | null {
        return +getDecodedToken().SecretaryId || null;
      },
      get isPharmacy(): boolean {
        const actions = getActionCodes();
        return actions !== null && actions.includes(ActionCode.PHARMACY_USER_CODE);
      },
      get isSecretariat(): boolean {
        const actions = getActionCodes();
        return actions !== null && actions.includes(ActionCode.SECRETARY_USER_CODE);
      },
      get isAdmin(): boolean {
        const actions = getActionCodes();
        return actions !== null && actions.includes(ActionCode.ADMIN_CODE);
      },
      get isSuperAdmin(): boolean {
        const actions = getActionCodes();
        return actions !== null && actions.includes(ActionCode.SUPER_ADMIN_CODE);
      },
    };
  })
  .actions((self) => {
    const env = getEnvironment(self);

    const authenticate = flow(function*(username: string, password: string) {
      const cookieName = `${env.config.configName}_web_gardes_jwt_token`;
      try {
        self.isAuthenticating = true;
        const token = yield env.api.post('authenticate', { username, password });
        self.impersonated = false;
        self.authToken = token;
        env.api.token = token!;
        env.plannerApi.token = token!;
        Cookie.set(cookieName, token);
        self.isAuthenticating = false;
        env.history.push(Page.Home);
      } catch (error) {
        env.history.push(Page.Login);
      }
      self.isAuthenticating = false;
    });

    const impersonate = flow(function*(target: string) {
      const cookieName = `${env.config.configName}_web_gardes_jwt_token`;
      try {
        self.isAuthenticating = true;
        const token = yield env.api.post('impersonate', { target });
        self.impersonated = true;
        self.authToken = token;
        env.api.token = token!;
        Cookie.set(cookieName, token);
        self.isAuthenticating = false;
        env.history.push(Page.Home);
      } catch (error) {
        env.history.push(Page.Login);
      }
      self.isAuthenticating = false;
    });

    const changePassword = flow(function*(username: string, password: string, newPwd1: string, newPwd2: string) {
      try {
        // const token = yield env.api.post('authenticate', { username, password });
        yield env.api.post('changePassword', { username, email: null, password, newPwd1, newPwd2 });
      } catch (error) {
        env.history.push(Page.Login);
      }
    });

    const resetPassword = flow(function*(username: string, email: string) {
      try {
        yield env.api.post('resetPassword/?username=' + username + '&email=' + email, {});
      } catch (error) {
        env.history.push(Page.Login);
      }
    });

    const resetPasswordByPharmacyId = flow(function*(pharmacyId: number) {
      try {
        yield env.api.post('resetPasswordByPharmacyId/' + pharmacyId, {});
      } catch (error) {
        env.history.push(Page.Login);
      }
    });

    const logout = flow(function*() {
      try {
        yield env.api.post('logout', { withCredentials: false });
        localStorage.clear();
        sessionStorage.clear();
        const cookies = document.cookie.split(';');

        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
          document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
        }
      } catch (error) {
        env.history.push(Page.Login);
      }
    });

    return {
      authenticate,
      impersonate,
      changePassword,
      resetPassword,
      resetPasswordByPharmacyId,
      logout,
      userHasActions(requireActionCodes: ActionCode[], authorizationType: AuthorizationType) {
        const isRequiredActionInCurrentUserActions = (requiredActionCode) =>
          self.userActionCodes.find((currentUserActionCode) => currentUserActionCode === requiredActionCode) !==
          undefined;

        return requireActionCodes.length
          ? authorizationType === 'all'
            ? requireActionCodes.every(isRequiredActionInCurrentUserActions)
            : requireActionCodes.some(isRequiredActionInCurrentUserActions)
          : true;
      },
      afterAttach() {
        const cookieName = `${env.config.configName}_web_gardes_jwt_token`;
        const token = Cookie.get(cookieName);
        if (token) {
          self.authToken = token;
          env.api.token = token;
          env.plannerApi.token = token;
        }
      },
    };
  })
  .views((self) => {
    return {
      isAuthenticated() {
        return self.userName !== '';
      },
    };
  });

export interface LoginModelType {
  login: string;
  password: string;
}
